import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, CampoDeSenha } from "../ui";
import {
  confirmarInscricaoDaConta,
  iniciarInscricaoDaConta,
  useAposInscricao,
  useDesativarMfa,
  useEstadoDoMfa,
  useRegerarCodigos,
} from "../../nucleo/acesso";
import { mensagemDoConsole } from "../../nucleo/console";
import { CodigosDeRecuperacao } from "./CodigosDeRecuperacao";
import { ConfigurarSegundoFator } from "./ConfigurarSegundoFator";

type Passo = "resumo" | "configurar" | "desligar" | "novos-codigos";

/** A verificação em duas etapas da própria conta: ligar, ver o estado, trocar os códigos, desligar. */
export function SegundoFatorDaConta() {
  const estado = useEstadoDoMfa();
  const [passo, setPasso] = useState<Passo>("resumo");
  const aposInscricao = useAposInscricao();

  if (estado.isPending) return <p role="status">Carregando…</p>;
  if (estado.isError) return <Aviso tom="erro">{mensagemDoConsole(estado.error)}</Aviso>;
  const { ativo, obrigatorio, codigos_restantes: restantes } = estado.data;

  if (passo === "configurar") {
    return (
      <ConfigurarSegundoFator
        chave={["mfa", "conta"]}
        iniciar={iniciarInscricaoDaConta}
        confirmar={confirmarInscricaoDaConta}
        aoConcluir={() => {
          aposInscricao();
          setPasso("resumo");
        }}
        rotuloDoFim="Concluir"
      />
    );
  }
  if (passo === "desligar") {
    return <DesligarSegundoFator aoTerminar={() => setPasso("resumo")} />;
  }
  if (passo === "novos-codigos") {
    return <NovosCodigos aoTerminar={() => setPasso("resumo")} />;
  }

  return (
    <div data-testid="segundo-fator-da-conta">
      <p data-testid="estado-do-segundo-fator">
        {ativo ? (
          <>
            <b>Ligada.</b> Você tem {restantes} {restantes === 1 ? "código" : "códigos"} de
            recuperação sem uso.
          </>
        ) : (
          <>
            <b>Desligada.</b> Além da senha, a entrada passa a pedir o código do seu celular.
          </>
        )}
      </p>
      {obrigatorio ? (
        <Aviso tom="marca" className="mt-3">
          A verificação em duas etapas é obrigatória para a sua conta e não pode ser desligada.
        </Aviso>
      ) : null}
      {ativo && restantes <= 2 ? (
        <Aviso tom="atencao" className="mt-3">
          Restam poucos códigos de recuperação. Gere um conjunto novo antes de precisar deles.
        </Aviso>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {!ativo ? (
          <Botao onClick={() => setPasso("configurar")} data-testid="ligar-segundo-fator">
            Ligar a verificação em duas etapas
          </Botao>
        ) : (
          <>
            <Botao variante="fantasma" onClick={() => setPasso("novos-codigos")}>
              Gerar códigos de recuperação novos
            </Botao>
            {!obrigatorio ? (
              <Botao variante="fantasma" onClick={() => setPasso("desligar")}>
                Desligar
              </Botao>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function DesligarSegundoFator({ aoTerminar }: { aoTerminar: () => void }) {
  const desativar = useDesativarMfa();
  const [senha, setSenha] = useState("");
  const [codigo, setCodigo] = useState("");

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    desativar.mutate({ senha, codigo: codigo.trim() }, { onSuccess: aoTerminar });
  }

  return (
    <form className="passo-do-mfa" onSubmit={enviar} noValidate data-testid="desligar-segundo-fator">
      <Aviso tom="atencao">
        Desligar tira a segunda trava da sua conta. Para confirmar, informe a senha e um código do
        aplicativo (ou um código de recuperação).
      </Aviso>
      <CampoDeSenha
        rotulo="Senha"
        valor={senha}
        aoMudar={setSenha}
        autoComplete="current-password"
      />
      <Campo
        rotulo="Código"
        inputMode="text"
        autoComplete="one-time-code"
        value={codigo}
        onChange={(evento) => setCodigo(evento.target.value)}
      />
      {desativar.isError ? <Aviso tom="erro">{mensagemDoConsole(desativar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao type="submit" disabled={!senha || !codigo.trim() || desativar.isPending}>
          {desativar.isPending ? "Desligando…" : "Desligar"}
        </Botao>
        <Botao variante="fantasma" onClick={aoTerminar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function NovosCodigos({ aoTerminar }: { aoTerminar: () => void }) {
  const regerar = useRegerarCodigos();
  const [codigo, setCodigo] = useState("");

  if (regerar.isSuccess) {
    return <CodigosDeRecuperacao codigos={regerar.data} rotuloDoFim="Concluir" aoContinuar={aoTerminar} />;
  }
  return (
    <form
      className="passo-do-mfa"
      onSubmit={(evento) => {
        evento.preventDefault();
        regerar.mutate(codigo.trim());
      }}
      noValidate
      data-testid="novos-codigos"
    >
      <Aviso tom="atencao">
        Os códigos de recuperação atuais deixam de valer. Informe o código do aplicativo para gerar
        um conjunto novo.
      </Aviso>
      <Campo
        rotulo="Código do aplicativo"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={codigo}
        onChange={(evento) => setCodigo(evento.target.value)}
      />
      {regerar.isError ? <Aviso tom="erro">{mensagemDoConsole(regerar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao type="submit" disabled={!codigo.trim() || regerar.isPending}>
          {regerar.isPending ? "Gerando…" : "Gerar códigos novos"}
        </Botao>
        <Botao variante="fantasma" onClick={aoTerminar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
